import clsx from 'clsx';
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import type { Person } from '@/api/types';

export type Tone = 'done' | 'active' | 'waiting' | 'overdue' | 'neutral';

const toneText: Record<Tone, string> = {
  done: 'text-done',
  active: 'text-active',
  waiting: 'text-waiting',
  overdue: 'text-overdue',
  neutral: 'text-muted',
};
const toneDot: Record<Tone, string> = {
  done: 'bg-done',
  active: 'bg-active',
  waiting: 'bg-waiting',
  overdue: 'bg-overdue',
  neutral: 'bg-faint',
};

/** The only place colour enters the interface. */
export function Status({ tone, children, className }: { tone: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 text-sm font-medium', toneText[tone], className)}>
      <span className={clsx('size-1.5 shrink-0 rounded-full', toneDot[tone])} aria-hidden />
      {children}
    </span>
  );
}

export function Avatar({ person, size = 'md' }: { person: Person; size?: 'sm' | 'md' }) {
  return (
    <span
      title={person.name}
      className={clsx(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-medium',
        person.side === 'studio' ? 'bg-ink text-inverse' : 'bg-sunken text-ink shadow-hair',
        size === 'sm' ? 'size-6 text-[10px]' : 'size-8 text-xs',
      )}
    >
      {person.initials}
    </span>
  );
}

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  return (
    <header className="mb-8">
      {back && <div className="mb-3">{back}</div>}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          {description && <p className="mt-1.5 max-w-2xl text-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export function Section({ title, aside, children, className }: { title: ReactNode; aside?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={clsx('mt-10 first:mt-0', className)}>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h2 className="text-md font-semibold tracking-tight">{title}</h2>
        {aside && <div className="text-sm text-muted">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-dashed border-strong px-6 py-10 text-center">
      <p className="font-medium">{title}</p>
      {children && <div className="mx-auto mt-1 max-w-md text-sm text-muted">{children}</div>}
    </div>
  );
}

export function Loading({ label = 'Загружаем…' }: { label?: string }) {
  return (
    <div className="space-y-3" aria-busy="true" aria-label={label}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-12 animate-pulse rounded bg-sunken" style={{ animationDelay: `${i * 120}ms` }} />
      ))}
    </div>
  );
}

export function Meta({ items }: { items: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="divide-y divide-line text-sm">
      {items.map((i) => (
        <div key={i.label} className="flex items-start justify-between gap-4 py-2.5">
          <dt className="text-muted">{i.label}</dt>
          <dd className="text-right">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Tabs<T extends string>({ id, value, onChange, items, label }: { id: string; value: T; onChange: (v: T) => void; items: { value: T; label: string; count?: number }[]; label?: string }) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Arrow keys move between tabs (and select, as the tabs here switch at once); Tab leaves the list.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = items.findIndex((t) => t.value === value);
    const next = e.key === 'ArrowRight' ? (i + 1) % items.length : e.key === 'ArrowLeft' ? (i - 1 + items.length) % items.length : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    onChange(items[next].value);
    refs.current[items[next].value]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} onKeyDown={onKeyDown} className="mb-6 flex gap-5 overflow-x-auto border-b border-line">
      {items.map((t) => (
        <button
          key={t.value}
          ref={(el) => {
            refs.current[t.value] = el;
          }}
          role="tab"
          id={`${id}-tab-${t.value}`}
          aria-selected={value === t.value}
          aria-controls={`${id}-panel`}
          tabIndex={value === t.value ? 0 : -1}
          onClick={() => onChange(t.value)}
          className={clsx(
            '-mb-px shrink-0 border-b-2 pb-2.5 text-sm font-medium transition-colors',
            value === t.value ? 'border-ink text-ink' : 'border-transparent text-muted hover:text-ink',
          )}
        >
          {t.label}
          {t.count !== undefined && <span className="num ml-1.5 text-faint">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Task state as a small glyph that reads at a glance in long lists:
 * hollow for not started, half for in progress, filled for done.
 */
export function TaskGlyph({ status, waiting }: { status: 'backlog' | 'todo' | 'in_progress' | 'review' | 'done'; waiting?: boolean }) {
  const colour = waiting ? 'text-waiting' : status === 'done' ? 'text-done' : status === 'in_progress' || status === 'review' ? 'text-active' : 'text-faint';
  return (
    <svg viewBox="0 0 14 14" className={clsx('size-3.5 shrink-0', colour)} aria-hidden>
      {status === 'done' ? (
        <>
          <rect x="1" y="1" width="12" height="12" rx="3" fill="currentColor" />
          <path d="M4.2 7.2 6.2 9.1 9.9 5" fill="none" stroke="rgb(var(--canvas))" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <>
          <rect x="1.5" y="1.5" width="11" height="11" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.3" strokeDasharray={status === 'backlog' ? '2 2' : undefined} />
          {(status === 'in_progress' || status === 'review') && <path d="M4 4h3v6H4z" fill="currentColor" />}
          {status === 'review' && <path d="M7 4h3v6H7z" fill="currentColor" opacity=".45" />}
        </>
      )}
    </svg>
  );
}

export function Sparkline({ values, className }: { values: number[]; className?: string }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const w = 96;
  const h = 28;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - 2 - ((v - min) / (max - min || 1)) * (h - 4)}`).join(' ');
  const last = pts.split(' ').at(-1)!.split(',');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={clsx('h-7 w-24 overflow-visible', className)} aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill="currentColor" />
    </svg>
  );
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** Open dialogs, the last one is on top: only it reacts to Escape and Tab. */
const openDialogs: symbol[] = [];

export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  if (!open) return null;
  return (
    <DialogShell onClose={onClose} title={title} wide={wide}>
      {children}
    </DialogShell>
  );
}

/**
 * Focus goes into the dialog when it opens (the first field, else the first control), Tab stays
 * inside, Escape closes it from wherever the focus is, and the focus returns to what opened it.
 */
function DialogShell({ onClose, title, children, wide }: { onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const panel = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });

  useEffect(() => {
    const token = Symbol('dialog');
    openDialogs.push(token);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = panel.current!;
    if (!el.contains(document.activeElement)) {
      const first = body.current?.querySelector<HTMLElement>('[data-autofocus], ' + FOCUSABLE) ?? el.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? el).focus();
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (openDialogs.at(-1) !== token) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = [...el.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => !n.closest('[hidden]'));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement;
      const outside = !el.contains(active);
      if (e.shiftKey && (outside || active === items[0] || active === el)) {
        e.preventDefault();
        items.at(-1)!.focus();
      } else if (!e.shiftKey && (outside || active === items.at(-1))) {
        e.preventDefault();
        items[0].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      openDialogs.splice(openDialogs.indexOf(token), 1);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]" onClick={onClose} />
      <div ref={panel} tabIndex={-1} className={clsx('max-h-dialog relative w-full overflow-y-auto rounded-t-lg bg-canvas shadow-pop focus:outline-none sm:rounded-lg', wide ? 'sm:max-w-2xl' : 'sm:max-w-md')}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="font-semibold">{title}</h2>
          <button className="btn-ghost size-8 px-0" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>
        <div ref={body}>{children}</div>
      </div>
    </div>
  );
}
